import {
  Loader2, User, Calendar, Plus, ArrowRightLeft, FileText,
  Type, FolderInput, Tag, UserCog, Paperclip,
} from 'lucide-react';
import { STATE_COLORS, STATE_LABELS, formatRevisionDate } from './useDocRevisions';

const CHANGE_CFG = {
  created:          { Icon: Plus,           label: () => 'Page créée' },
  state_changed:    { Icon: ArrowRightLeft, label: (c) => `${STATE_LABELS[c.from] || c.from} → ${STATE_LABELS[c.to] || c.to}` },
  content_updated:  { Icon: FileText,       label: () => 'Contenu modifié' },
  title_changed:    { Icon: Type,           label: (c) => `« ${c.from} » → « ${c.to} »` },
  moved:            { Icon: FolderInput,    label: (c) => `${c.from || '—'} → ${c.to || '—'}` },
  category_changed: { Icon: Tag,            label: (c) => `${c.from || '—'} → ${c.to || '—'}` },
  owner_changed:    { Icon: UserCog,        label: (c) => `${c.from || '—'} → ${c.to || '—'}` },
  files_changed:    { Icon: Paperclip,      label: (c) => {
    const parts = [];
    if (c.added)   parts.push(`+${c.added}`);
    if (c.removed) parts.push(`-${c.removed}`);
    return `Fichiers : ${parts.join(', ')}`;
  }},
};

function ChangeList({ changes, isCompact }) {
  if (!changes?.length) return null;

  const visible = isCompact ? changes.slice(0, 2) : changes;
  const hidden = isCompact ? changes.length - visible.length : 0;
  const textCls = isCompact ? 'text-[10px]' : 'text-[11px]';
  const iconSz = isCompact ? 9 : 10;
  const gapCls = isCompact ? 'gap-0.5' : 'gap-1';

  return (
    <div className={`flex flex-col ${gapCls} mt-1`}>
      {visible.map((change, i) => {
        const cfg = CHANGE_CFG[change.type];
        if (!cfg) return null;
        const { Icon, label } = cfg;
        return (
          <span key={i} className={`inline-flex items-center gap-1 ${textCls} text-stone-400`}>
            <Icon size={iconSz} className="flex-shrink-0" />
            <span className="truncate">{label(change)}</span>
          </span>
        );
      })}
      {hidden > 0 && (
        <span className={`${textCls} text-stone-300 italic`}>+{hidden} autre{hidden > 1 ? 's' : ''}</span>
      )}
    </div>
  );
}

/**
 * Reusable revision timeline.
 * @param {Object} props
 * @param {Array}  props.revisions
 * @param {boolean} props.loading
 * @param {string|null} props.error
 * @param {'compact'|'full'} [props.variant='compact']
 */
export default function RevisionTimeline({ revisions, loading, error, variant = 'compact' }) {
  const isCompact = variant === 'compact';
  const padLeft = isCompact ? 'pl-5' : 'pl-6';
  const padBottom = isCompact ? 'pb-4' : 'pb-6';
  const contentMargin = isCompact ? 'ml-2' : 'ml-4';
  const badgeCls = isCompact
    ? 'px-1.5 py-0.5 rounded-full text-[10px] font-medium leading-tight'
    : 'px-2 py-0.5 rounded-full text-xs font-medium';
  const metaCls = isCompact
    ? 'flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-stone-500 mt-1'
    : 'flex items-center gap-3 text-xs text-stone-500 mt-1';
  const iconSize = isCompact ? 10 : 11;
  const logCls = isCompact
    ? 'text-[11px] text-stone-600 mt-0.5 leading-snug'
    : 'text-xs text-stone-600 mt-1';

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${isCompact ? 'py-10' : 'py-12'}`}>
        <Loader2 size={isCompact ? 20 : 24} className="animate-spin text-stone-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center ${isCompact ? 'py-8 px-4' : 'py-12'}`}>
        <p className={`text-stone-400 ${isCompact ? 'text-xs' : 'text-sm'}`}>{error}</p>
      </div>
    );
  }

  if (revisions.length === 0) {
    return (
      <p className={`text-stone-400 text-center ${isCompact ? 'text-xs py-8' : 'text-sm py-12'}`}>
        Aucune révision trouvée.
      </p>
    );
  }

  return (
    <div className={isCompact ? 'px-3 py-3' : ''}>
      {revisions.map((rev, i) => {
        const isCreation = rev.isCreation;
        const label = isCreation
          ? 'Création'
          : (STATE_LABELS[rev.moderationState] || rev.moderationState || 'Brouillon');
        const colorCls = isCreation
          ? 'bg-blue-100 text-blue-700'
          : (STATE_COLORS[rev.moderationState] || STATE_COLORS.draft);
        const formattedDate = formatRevisionDate(rev.date);
        const dotColor = isCreation ? 'border-blue-400' : 'border-stone-300';
        const dotSize = isCreation
          ? (isCompact ? 'w-3 h-3' : 'w-3.5 h-3.5')
          : (isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3');

        const nonCreationChanges = rev.changes?.filter((c) => c.type !== 'created') || [];

        return (
          <div
            key={rev.vid || i}
            className={`relative ${padLeft} ${padBottom} ${i < revisions.length - 1 ? 'border-l-2 border-stone-200' : ''}`}
          >
            <div className={`absolute left-0 top-0.5 -translate-x-1/2 ${dotSize} rounded-full bg-white border-2 ${dotColor}`} />
            <div className={contentMargin}>
              <span className={`inline-block ${badgeCls} ${colorCls}`}>
                {label}
              </span>
              <div className={metaCls}>
                <span className="flex items-center gap-0.5">
                  <User size={iconSize} />
                  {rev.author || 'Inconnu'}
                </span>
                <span className="flex items-center gap-0.5" title={rev.date || ''}>
                  <Calendar size={iconSize} />
                  {formattedDate}
                </span>
              </div>
              {rev.log && (
                <p className={logCls}>{rev.log}</p>
              )}
              <ChangeList changes={nonCreationChanges} isCompact={isCompact} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
